import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RestaurantFactory } from '../../../services/restaurant';
import { PaymentProcessor } from '../../../types/restaurant';
import { CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../../config/firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import { POSConfig } from '../../../services/pos/types';

// Add this helper
const createProcessorSecret = async (
  restaurantId: string,
  processor: string,
  credentials: Record<string, string>
) => {
  const functions = getFunctions();
  const createSecret = httpsCallable(functions, 'createProcessorSecret');
  
  const result = await createSecret({
    restaurantId,
    processor,
    credentials
  });
  
  return (result.data as any).secretName; // Returns: "azul-auth-rest-abc123"
};

export default function CreateRestaurantScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [name, setName] = useState('');
  const [processor, setProcessor] = useState<PaymentProcessor>('none');
  
  // Stripe
  const [stripePublicKey, setStripePublicKey] = useState('');
  const [stripeEnvironment, setStripeEnvironment] = useState<'test' | 'live'>('test');
  
  // CardNet
  const [cardnetMerchantId, setCardnetMerchantId] = useState('');
  
  // Azul
  const [azulAuth1, setAzulAuth1] = useState('');
  const [azulAuth2, setAzulAuth2] = useState('');
  const [azulMerchantId, setAzulMerchantId] = useState('');
  const [azulChannel, setAzulChannel] = useState('EC');
  
  // Secret keys (never saved to Firestore)
  const [stripeSecretKey, setStripeSecretKey] = useState('');
  const [cardnetApiSecret, setCardnetApiSecret] = useState('');
  
  // Settings
  const [currency, setCurrency] = useState('DOP');
  const [taxRate, setTaxRate] = useState('18');

  // ========== POS INTEGRATION (SUPER ADMIN ONLY) ==========
  const [integrationMode, setIntegrationMode] = useState<'api' | 'webhook' | 'polling' | 'none'>('api');

  // POS Config
  const [posForm, setPosForm] = useState({
    provider: 'mock' as 'mock' | 'none',
    enabled: true,
    autoSync: true,
    syncInterval: 5,
    apiUrl: '',
    apiKey: ''
  });

  // Owner user creation
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');
  const [ownerName, setOwnerName] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Step 1: Create secrets in GSM FIRST (if credentials provided)
      let secretName: string | undefined;
      if (processor === 'stripe' && stripeSecretKey) {
        // Create Stripe secret with temp ID
        const tempId = `temp-${Date.now()}`;
        secretName = await createProcessorSecret(tempId, 'stripe', {
          secretKey: stripeSecretKey.trim()
        });
      } else if (processor === 'cardnet' && cardnetApiSecret) {
        // Create CardNet secret with temp ID
        const tempId = `temp-${Date.now()}`;
        secretName = await createProcessorSecret(tempId, 'cardnet', {
          apiSecret: cardnetApiSecret.trim()
        });
      } else if (processor === 'azul' && azulAuth1 && azulAuth2) {
        // Create Azul secret with temp ID
        const tempId = `temp-${Date.now()}`;
        secretName = await createProcessorSecret(tempId, 'azul', {
          auth1: azulAuth1.trim(),
          auth2: azulAuth2.trim()
        });
      }

      // Step 2: Build restaurant data (PUBLIC data + secretName reference)
      const input: any = {
        name: name.trim(),
        processor,
        integrationMode,  // CRITICAL: Set by super admin, inherited by mesas
        settings: {
          currency,
          taxRate: parseFloat(taxRate),
        }
      };

      // Add processor config (PUBLIC data only + secretName)
      if (processor === 'stripe') {
        if (!stripePublicKey) {
          throw new Error('La clave pública de Stripe es requerida');
        }
        input.stripeConfig = {
          publicKey: stripePublicKey.trim(),
          environment: stripeEnvironment,
          merchantId: '',
          ...(secretName && { secretName }), // Only add if exists
        };
      } else if (processor === 'cardnet') {
        if (!cardnetMerchantId) {
          throw new Error('El ID de comerciante de CardNet es requerido');
        }
        input.cardnetConfig = {
          merchantId: cardnetMerchantId.trim(),
          environment: 'sandbox',
          ...(secretName && { secretName }), // Only add if exists
        };
      } else if (processor === 'azul') {
        if (!azulMerchantId) {
          throw new Error('El Merchant ID de Azul es requerido');
        }
        if (!azulAuth1 || !azulAuth2) {
          throw new Error('Todas las credenciales de Azul son requeridas');
        }
        input.azulConfig = {
          merchantId: azulMerchantId.trim(),
          channel: azulChannel,
          environment: 'sandbox',
          ...(secretName && { secretName }), // Only add if exists
        };
      }

      // Step 3: Create restaurant in Firestore
      const restaurantId = await RestaurantFactory.createRestaurant(input);

      // Step 4: Create owner user if provided
      if (ownerEmail && ownerPassword && ownerName) {
        const functions = getFunctions();
        const createOwner = httpsCallable(functions, 'createRestaurantOwner');
        
        await createOwner({
          email: ownerEmail.trim(),
          password: ownerPassword,
          displayName: ownerName.trim(),
          restaurantId: restaurantId
        });
      }

      // Step 5: Validate Mock POS fields if selected
      if (posForm.provider === 'mock') {
        if (!posForm.apiUrl) {
          throw new Error('Mock POS API URL es requerido');
        }
        if (!posForm.apiKey) {
          throw new Error('Mock POS API Key es requerido');
        }
        if (!posForm.apiUrl.startsWith('http://') && !posForm.apiUrl.startsWith('https://')) {
          throw new Error('API URL debe comenzar con http:// o https://');
        }
      }

      // Step 6: Update POS config if provided
      // NOTE: integrationMode is now at Restaurant level, not in POSConfig
      if (posForm.provider !== 'none') {
        const posConfig: POSConfig = {
          provider: posForm.provider,
          enabled: posForm.enabled,
          credentials: {
            ...(posForm.provider === 'mock' && {
              apiUrl: posForm.apiUrl,
              apiKey: posForm.apiKey
            })
          },
          syncSettings: {
            autoSync: posForm.autoSync,
            syncIntervalMinutes: posForm.syncInterval
          }
        };

        const restaurantRef = doc(db, 'restaurants', restaurantId);
        await updateDoc(restaurantRef, {
          posConfig
        });
      }
      
      setSuccess(`¡Restaurante creado exitosamente! ID: ${restaurantId}`);
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/super-admin');
      }, 2000);
    } catch (err: any) {
      console.error('Error creating restaurant:', err);
      setError(err.message || 'Error al crear el restaurante');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8 max-w-4xl mx-auto">
      {/* Header */}
      <button
        onClick={() => navigate('/super-admin')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft size={20} />
        Volver al Panel
      </button>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Crear Nuevo Restaurante</h1>
        <p className="text-gray-600 mt-2">
          Agregar un nuevo restaurante a la plataforma YAP. Todos los restaurantes comienzan con Mock POS habilitado.
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="text-green-800 font-medium">¡Éxito!</p>
            <p className="text-green-700 text-sm mt-1">{success}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
          <div className="flex-1">
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 space-y-8">
        {/* Basic Info */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Información Básica</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre del Restaurante *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="ej., Palacio de la Pizza"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* POS Integration Mode (Super Admin Only) */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Integración POS</h2>
          <p className="text-sm text-gray-600 mb-4">
            🔑 Solo super admins pueden configurar esto. Los restaurantes heredan esta configuración.
          </p>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Modo de Integración *
            </label>
            <div className="grid grid-cols-2 gap-4">
              {([
                { value: 'api', label: 'API', desc: 'Sincronización en tiempo real' },
                { value: 'webhook', label: 'Webhook', desc: 'Eventos push del POS' },
                { value: 'polling', label: 'Polling', desc: 'Verificación periódica' },
                { value: 'none', label: 'Ninguno', desc: 'Sin integración POS (manual)' }
              ] as const).map((mode) => (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => setIntegrationMode(mode.value)}
                  className={`p-4 border-2 rounded-lg text-left transition ${
                    integrationMode === mode.value
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold text-gray-900">{mode.label}</div>
                  <div className="text-sm text-gray-500 mt-1">{mode.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {integrationMode === 'none' && (
            <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                ⚠️ <strong>Modo Manual:</strong> Las mesas no se sincronizarán con ningún POS.
                Los totales deben ingresarse manualmente.
              </p>
            </div>
          )}
        </div>

        {/* Payment Processor */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Procesador de Pagos</h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Seleccionar Procesador *
            </label>
            <div className="grid grid-cols-2 gap-4">
              {(['stripe', 'cardnet', 'azul', 'none'] as const).map((proc) => (
                <button
                  key={proc}
                  type="button"
                  onClick={() => setProcessor(proc)}
                  className={`p-4 border-2 rounded-lg text-left transition ${
                    processor === proc
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold text-gray-900 capitalize">{proc}</div>
                  <div className="text-sm text-gray-500 mt-1">
                    {proc === 'stripe' ? 'Pagos globales' :
                     proc === 'cardnet' ? 'República Dominicana' :
                     proc === 'azul' ? 'República Dominicana' :
                     'Configurar más tarde'}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Stripe Config */}
          {processor === 'stripe' && (
            <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900">Configuración de Stripe</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Clave Pública *
                </label>
                <input
                  type="text"
                  value={stripePublicKey}
                  onChange={(e) => setStripePublicKey(e.target.value)}
                  placeholder="pk_test_... (Clave pública de Stripe)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">Encuéntralo en tu Panel de Stripe</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Clave Secreta *
                </label>
                <input
                  type="password"
                  value={stripeSecretKey}
                  onChange={(e) => setStripeSecretKey(e.target.value)}
                  placeholder="sk_test_... (Clave secreta de Stripe)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  🔒 Se almacena de forma segura en Google Secret Manager
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ambiente
                </label>
                <select
                  value={stripeEnvironment}
                  onChange={(e) => setStripeEnvironment(e.target.value as 'test' | 'live')}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="test">Modo Prueba</option>
                  <option value="live">Modo Producción</option>
                </select>
              </div>
            </div>
          )}

          {/* CardNet Config */}
          {processor === 'cardnet' && (
            <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900">Configuración de CardNet</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ID de Comerciante *
                </label>
                <input
                  type="text"
                  value={cardnetMerchantId}
                  onChange={(e) => setCardnetMerchantId(e.target.value)}
                  placeholder="CARDNET_TEST_001"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Credenciales de API almacenadas de forma segura en Cloud Functions
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  API Secret (Clave Privada) *
                </label>
                <input
                  type="password"
                  value={cardnetApiSecret}
                  onChange={(e) => setCardnetApiSecret(e.target.value)}
                  placeholder="API Secret / Private Key"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  🔒 Se almacena de forma segura en Google Secret Manager
                </p>
              </div>
            </div>
          )}

          {/* Azul Config */}
          {processor === 'azul' && (
            <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900">Configuración de Azul</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Auth1 *
                </label>
                <input
                  type="text"
                  value={azulAuth1}
                  onChange={(e) => setAzulAuth1(e.target.value)}
                  placeholder="Tu Auth1 de Azul (no se guarda en Firestore)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Auth2 *
                </label>
                <input
                  type="text"
                  value={azulAuth2}
                  onChange={(e) => setAzulAuth2(e.target.value)}
                  placeholder="Tu Auth2 de Azul (no se guarda en Firestore)"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  🔒 Auth1 y Auth2 se almacenan de forma segura en Google Secret Manager
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Merchant ID *
                </label>
                <input
                  type="text"
                  value={azulMerchantId}
                  onChange={(e) => setAzulMerchantId(e.target.value)}
                  placeholder="39038540039"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Canal
                </label>
                <input
                  type="text"
                  value={azulChannel}
                  onChange={(e) => setAzulChannel(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>
          )}
        </div>

        {/* Settings */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Configuración del Restaurante</h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Moneda
              </label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              >
                <option value="DOP">DOP (Dominican Peso)</option>
                <option value="USD">USD (US Dollar)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tasa de Impuesto (%)
              </label>
              <input
                type="number"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                min="0"
                max="100"
                step="0.1"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              />
            </div>
          </div>

          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">✓ Características Predeterminadas Habilitadas:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Mock POS con sincronización automática (intervalos de 5 min)</li>
              <li>• División de cuenta (por artículo o por igual)</li>
              <li>• Opciones de propina (10%, 15%, 20%)</li>
              <li>• Tarifa de plataforma: 2.5%</li>
              <li>• Sistema de calificación de personal</li>
            </ul>
          </div>
        </div>

        {/* POS Config */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Configuración de POS</h2>
          
          <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Proveedor POS
              </label>
              <select
                value={posForm.provider}
                onChange={(e) => setPosForm({ ...posForm, provider: e.target.value as 'mock' | 'none' })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
              >
                <option value="mock">Mock POS</option>
                <option value="none">Sin POS</option>
              </select>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="posEnabled"
                checked={posForm.enabled}
                onChange={(e) => setPosForm({ ...posForm, enabled: e.target.checked })}
                className="w-5 h-5 text-blue-600 rounded"
              />
              <label htmlFor="posEnabled" className="text-sm font-medium text-gray-700">
                Habilitar integración con POS
              </label>
            </div>

            {posForm.provider === 'mock' && (
              <>
                {/* Mock POS API URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mock POS API URL *
                  </label>
                  <input
                    type="text"
                    value={posForm.apiUrl || ''}
                    onChange={(e) => setPosForm({ ...posForm, apiUrl: e.target.value })}
                    placeholder="http://10.0.0.252:3001"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    URL base de tu Mock POS API (incluye http:// o https://)
                  </p>
                </div>

                {/* API Key */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Key *
                  </label>
                  <input
                    type="password"
                    value={posForm.apiKey}
                    onChange={(e) => setPosForm({ ...posForm, apiKey: e.target.value })}
                    placeholder="mock_pos_key_restaurant_xyz"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    🔒 Clave de autenticación para Mock POS (se guarda de forma segura)
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="autoSync"
                    checked={posForm.autoSync}
                    onChange={(e) => setPosForm({ ...posForm, autoSync: e.target.checked })}
                    className="w-5 h-5 text-blue-600 rounded"
                  />
                  <label htmlFor="autoSync" className="text-sm font-medium text-gray-700">
                    Sincronización automática
                  </label>
                </div>

                {posForm.autoSync && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Intervalo de sincronización (minutos)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={posForm.syncInterval}
                      onChange={(e) => setPosForm({ ...posForm, syncInterval: parseInt(e.target.value) || 5 })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Owner User Creation */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Propietario del Restaurante</h2>
          <p className="text-sm text-gray-600 mb-4">
            Crea una cuenta de propietario para este restaurante (opcional, puede crearse después)
          </p>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre del Propietario
              </label>
              <input
                type="text"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                placeholder="ej., Juan Pérez"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email del Propietario
              </label>
              <input
                type="email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                placeholder="ej., juan@restaurant.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña Temporal
              </label>
              <input
                type="password"
                value={ownerPassword}
                onChange={(e) => setOwnerPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                minLength={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                El propietario deberá cambiar esta contraseña en el primer inicio de sesión
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate('/super-admin')}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading || !name.trim()}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium"
          >
            {loading ? 'Creando...' : 'Crear Restaurante'}
          </button>
        </div>
      </form>
    </div>
  );
}